import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type * as z from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {
  DeleteLineAccountResponse,
  ExternalServicesResponse,
  PutLineAccountResponse,
} from '../entities/externalServices/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

export type ReturnValueType = ResultWithOauthToken<
  ExternalServicesResponseType,
  AppError
>;
export type PutLineAccountResponseType = z.infer<typeof PutLineAccountResponse>;
export type DeleteLineAccountResponseType = z.infer<
  typeof DeleteLineAccountResponse
>;

export type ExternalServicesResponseType = z.infer<
  typeof ExternalServicesResponse
>;
export type ResultPutLineAccountResponse = ResultWithOauthToken<
  PutLineAccountResponseType,
  AppError
>;
export type ResultDeleteLineAccountResponse = ResultWithOauthToken<
  DeleteLineAccountResponseType,
  AppError
>;

type GetExternalServicesArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};
type PutLineAccountArgs = {
  cookies: Partial<Record<string, string>>;
  code: string;
  version?: string;
};
type DeleteLineAccountArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};
type UseGetExternalServicesOptions = {
  config?: UseQueryOptions<ReturnValueType>;
} & Omit<GetExternalServicesArgs, 'cookies'>;

type UsePutLineAccountOptions = {
  config?: UseQueryOptions<ResultPutLineAccountResponse>;
} & Omit<PutLineAccountArgs, 'cookies'>;

type UseDeleteLineAccountOptions = {
  config?: UseQueryOptions<ResultDeleteLineAccountResponse>;
} & Omit<DeleteLineAccountArgs, 'cookies'>;

export const getExternalServices = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetExternalServicesArgs): Promise<ReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<ExternalServicesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/service/users/me/external-services`;
    return defaultClient.get(path, ExternalServicesResponse);
  };

  const result = await sendApiRequest<ExternalServicesResponseType>({
    request,
    cookies,
    authRequired: true,
  });

  return result;
};

export const useGetExternalServices = ({
  config,
}: UseGetExternalServicesOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ReturnValueType>(['externalServices'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getExternalServices({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const putLineAccount = async ({
  cookies,
  code,
  version = DEFAULT_API_VERSION,
}: PutLineAccountArgs): Promise<ResultPutLineAccountResponse> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutLineAccountResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.put(
      `/${version}/service/users/me/external-services/line`,
      PutLineAccountResponse,
      {code},
    );
  };

  const result = await sendApiRequest<PutLineAccountResponseType>({
    request,
    cookies,
    authRequired: true,
  });

  return result;
};

export const usePutLineAccount = ({
  code,
  version = DEFAULT_API_VERSION,
}: UsePutLineAccountOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<ResultPutLineAccountResponse>(async () => {
    const cookies = parseCookies();
    const result = await putLineAccount({cookies, code, version});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

export const deleteLineAccount = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: DeleteLineAccountArgs): Promise<ResultDeleteLineAccountResponse> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<DeleteLineAccountResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.delete(
      `/${version}/service/users/me/external-services/line`,
      DeleteLineAccountResponse,
    );
  };

  const result = await sendApiRequest<DeleteLineAccountResponseType>({
    request,
    cookies,
    authRequired: true,
  });

  return result;
};

export const useDeleteLineAccount = ({
  version = DEFAULT_API_VERSION,
}: UseDeleteLineAccountOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<ResultDeleteLineAccountResponse>(async () => {
    const cookies = parseCookies();
    const result = await deleteLineAccount({cookies, version});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};
